import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface SelectablePillOption {
    value: string;
    label: string;
    color?: string;
    description?: string;
    disabled?: boolean;
}

type SelectablePillGridColumns = 'auto' | 1 | 2;
export type SelectablePillGridSize = 'small' | 'medium' | 'large';
export type SelectablePillGridSelectionMode = 'multiple' | 'single';

@Component({
    selector: 'app-selectable-pill-grid',
    templateUrl: './selectable-pill-grid.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectablePillGridComponent {
    @Input() options: SelectablePillOption[] = [];
    @Input() selectedValues: string[] = [];
    @Input() disabled = false;
    @Input() columns: SelectablePillGridColumns = 'auto';
    @Input() size: SelectablePillGridSize = 'medium';
    @Input() selectionMode: SelectablePillGridSelectionMode = 'multiple';
    @Input() maxHeightClass = '';
    @Input() emptyText = '';

    @Output() selectedValuesChange = new EventEmitter<string[]>();
    @Output() optionToggled = new EventEmitter<SelectablePillOption>();

    readonly selectedIcon: IconProp = ['fal', 'check'];
    readonly unselectedIcon: IconProp = ['fal', 'plus'];

    get layoutClass(): string {
        if (this.columns === 2) {
            return 'grid md:grid-cols-2';
        }

        if (this.columns === 1) {
            return 'grid grid-cols-1';
        }

        return 'flex flex-wrap';
    }

    get pillClass(): string {
        switch (this.size) {
            case 'small':
                return 'gap-1.5 rounded-md px-2 py-1.5';
            case 'large':
                return 'gap-3 rounded-lg px-3 py-3';
            default:
                return 'gap-2 rounded-md p-2.5';
        }
    }

    get dotClass(): string {
        switch (this.size) {
            case 'small':
                return 'h-2 w-2';
            case 'large':
                return 'h-3 w-3';
            default:
                return 'h-2.5 w-2.5';
        }
    }

    get labelClass(): string {
        switch (this.size) {
            case 'small':
                return 'text-xs';
            case 'large':
                return 'text-base';
            default:
                return 'text-sm';
        }
    }

    get descriptionClass(): string {
        switch (this.size) {
            case 'small':
                return 'text-[11px]';
            case 'large':
                return 'text-sm';
            default:
                return 'text-xs';
        }
    }

    get iconClass(): string {
        switch (this.size) {
            case 'small':
                return 'text-[10px]';
            case 'large':
                return 'text-sm';
            default:
                return 'text-xs';
        }
    }

    isSelected(option: SelectablePillOption): boolean {
        return (this.selectedValues || []).includes(option.value);
    }

    isOptionDisabled(option: SelectablePillOption): boolean {
        return this.disabled || option.disabled === true;
    }

    toggle(option: SelectablePillOption): void {
        if (this.isOptionDisabled(option)) {
            return;
        }

        const currentValues = this.selectedValues || [];
        const nextValues = this.selectionMode === 'single'
            ? [option.value]
            : (this.isSelected(option)
                ? currentValues.filter((value) => value !== option.value)
                : [...currentValues, option.value]);

        this.selectedValuesChange.emit(nextValues);
        this.optionToggled.emit(option);
    }

    trackByValue(_: number, option: SelectablePillOption): string {
        return option.value;
    }
}
